import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../providers/auth-provider';
import { useConfig } from '../providers/config-provider';
import { useNotification } from '../providers/notification-provider';
import SessionStateScreen from '../components/auth/SessionStateScreen';
import { loginRequest, useApiClient } from '../lib/api';
import { extractApiErrorMessage } from '../utils/apiErrorUtils';
import { readPanelScope, writePanelScope } from '../utils/panelScopeStorage';
import logo from '../assets/logo.png';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  TvIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

/* ─── constants ─── */

const STEP_LOGIN = 'login';
const STEP_COMPANY = 'company';
const STEP_RESTAURANT = 'restaurant';

const loginSchema = z.object({
  email: z.string().email('Informe um email válido'),
  password: z.string().min(1, 'Informe a senha'),
});

/* ─── helpers ─── */

/* ─── component ─── */

export default function PanelLoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { apiBase, setCompanyId, setOperationalScope } = useConfig();
  const { showToast } = useNotification();

  const [step, setStep] = useState(STEP_LOGIN);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [isResolvingScope, setIsResolvingScope] = useState(false);

  const api = useApiClient();
  const autoResolveRef = useRef({ inFlight: false, cycle: 0 });
  const navigationCommittedRef = useRef(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => {
    document.title = 'Painel de Chamadas | Orion WaiterCall';
  }, []);

  /* ─── scope resolution ─── */

  const isAdminMaster = auth.user?.isAdminMaster ?? false;

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const res = await api.get('/api/v1/auth/companies', {
        params: { page: 1, pageSize: 200 },
      });
      const items = res.data?.items ?? [];
      setCompanies(items);
      return items;
    } catch {
      showToast('Erro ao carregar empresas.', 'error');
      return [];
    } finally {
      setLoadingCompanies(false);
    }
  }, [api, showToast]);

  const fetchRestaurants = useCallback(async (companyId) => {
    setLoadingRestaurants(true);
    try {
      const res = await api.get('/api/v1/auth/restaurants', {
        params: { companyId, page: 1, pageSize: 200 },
      });
      const items = res.data?.items ?? [];
      setRestaurants(items);
      return items;
    } catch {
      showToast('Erro ao carregar restaurantes.', 'error');
      return [];
    } finally {
      setLoadingRestaurants(false);
    }
  }, [api, showToast]);

  const resolveAndNavigate = useCallback(
    (companyObj, restaurantObj) => {
      navigationCommittedRef.current = true;
      setOperationalScope(companyObj.id, restaurantObj.id);
      writePanelScope({
        companyId: companyObj.id,
        companyName: companyObj.name,
        restaurantId: restaurantObj.id,
        restaurantName: restaurantObj.name,
      });
      navigate('/painel', { replace: true });
    },
    [navigate, setOperationalScope]
  );

  const cancelAutoResolve = useCallback(() => {
    autoResolveRef.current.cycle += 1;
    autoResolveRef.current.inFlight = false;
    setIsResolvingScope(false);
  }, []);

  const autoResolveScope = useCallback(async () => {
    if (!auth.user) {
      return;
    }

    const cycle = autoResolveRef.current.cycle + 1;
    autoResolveRef.current = { inFlight: true, cycle };
    setIsResolvingScope(true);

    const isStale = () => autoResolveRef.current.cycle !== cycle;
    let navigated = false;

    try {
      /* Try previously-stored panel scope */
      const stored = readPanelScope();
      if (stored?.companyId && stored?.restaurantId) {
        navigationCommittedRef.current = true;
        setOperationalScope(stored.companyId, stored.restaurantId);
        navigate('/painel', { replace: true });
        navigated = true;
        return;
      }

      /* Fetch companies to determine flow */
      const fetchedCompanies = await fetchCompanies();
      if (isStale()) {
        return;
      }

      if (fetchedCompanies.length === 1) {
        const comp = fetchedCompanies[0];
        setCompanyId(comp.id);
        const fetchedRestaurants = await fetchRestaurants(comp.id);
        if (isStale()) {
          return;
        }

        if (fetchedRestaurants.length === 1) {
          resolveAndNavigate(comp, fetchedRestaurants[0]);
          navigated = true;
          return;
        }

        if (fetchedRestaurants.length > 1) {
          setSelectedCompany(comp);
          setStep(STEP_RESTAURANT);
          return;
        }
      }

      if (fetchedCompanies.length > 1) {
        setStep(STEP_COMPANY);
        return;
      }

      /* No companies found — stay on login */
      showToast('Nenhuma empresa encontrada para este usuário.', 'error');
    } finally {
      if (autoResolveRef.current.cycle === cycle) {
        autoResolveRef.current.inFlight = false;
        if (!navigated) {
          setIsResolvingScope(false);
        }
      }
    }
  }, [
    auth.user,
    fetchCompanies,
    fetchRestaurants,
    navigate,
    resolveAndNavigate,
    setCompanyId,
    setOperationalScope,
    showToast,
  ]);

  /* ─── login mutation ─── */

  const loginMutation = useMutation({
    mutationFn: async (values) => {
      const res = await loginRequest(apiBase, values.email, values.password, true);
      return res.data;
    },
    onSuccess: (data) => {
      try {
        auth.login(data, { rememberMe: true });
      } catch (error) {
        const message = extractApiErrorMessage(
          error,
          'Não foi possível concluir o login do painel com a resposta recebida do servidor.'
        );
        setError('root', { type: 'manual', message });
        showToast(message, 'error');
      }
    },
    onError: (error) => {
      const message = extractApiErrorMessage(
        error,
        'Não foi possível autenticar. Verifique as credenciais e tente novamente.'
      );
      setError('root', { type: 'manual', message });
      showToast(message, 'error');
    },
  });

  /* After login completes and user is loaded, resolve scope.
     Also handles already-authenticated users on mount (e.g. cookie). */
  useEffect(() => {
    if (
      !auth.isAuthenticated ||
      auth.isLoading ||
      step !== STEP_LOGIN ||
      navigationCommittedRef.current
    ) {
      return;
    }

    if (autoResolveRef.current.inFlight) {
      return;
    }

    autoResolveScope().catch(() => {
      cancelAutoResolve();
      showToast('Não foi possível preparar o painel agora.', 'error');
    });

    return () => {
      cancelAutoResolve();
    };
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    autoResolveScope,
    cancelAutoResolve,
    showToast,
    step,
  ]);

  const onSubmitLogin = (values) => {
    clearErrors('root');
    loginMutation.mutate(values);
  };

  /* ─── company selection ─── */

  const handleSelectCompany = async (company) => {
    cancelAutoResolve();
    setSelectedCompany(company);
    setCompanyId(company.id);
    const fetchedRestaurants = await fetchRestaurants(company.id);
    if (fetchedRestaurants.length === 1) {
      resolveAndNavigate(company, fetchedRestaurants[0]);
    } else {
      setStep(STEP_RESTAURANT);
    }
  };

  /* ─── restaurant selection ─── */

  const handleSelectRestaurant = (restaurant) => {
    const comp = selectedCompany || companies.find((c) => c.id === restaurant.companyId) || {
      id: restaurant.companyId,
      name: '',
    };
    resolveAndNavigate(comp, restaurant);
  };

  /* ─── step indicator ─── */

  const currentStepNumber = step === STEP_LOGIN ? 1 : step === STEP_COMPANY ? 2 : 3;

  const totalSteps = useMemo(() => {
    let count = 1; // login
    if (companies.length > 1 || isAdminMaster) count++;
    if (restaurants.length > 0) count++;
    return Math.max(count, currentStepNumber);
  }, [companies.length, isAdminMaster, restaurants.length, currentStepNumber]);

  /* ─── render ─── */

  if (auth.isLoading) {
    return (
      <SessionStateScreen
        variant="loading"
        title="Validando acesso ao painel"
        message="Estamos confirmando a sessão atual antes de resolver o escopo da empresa e do restaurante."
      />
    );
  }

  if (isResolvingScope) {
    return (
      <SessionStateScreen
        variant="loading"
        title="Preparando o painel"
        message="Estamos resolvendo a empresa e o restaurante do painel antes de concluir a navegação."
      />
    );
  }

  if (auth.sessionIssue?.type === 'recoverable') {
    return (
      <SessionStateScreen
        variant="recoverable"
        title={auth.sessionIssue.title}
        message={auth.sessionIssue.message}
        primaryAction={{
          label: 'Tentar novamente',
          onClick: auth.retrySession,
          icon: <ArrowRightIcon className="h-4 w-4" />,
        }}
        secondaryAction={
          auth.isAuthenticated
            ? {
                label: 'Encerrar sessão',
                onClick: auth.logout,
              }
            : undefined
        }
      />
    );
  }

  if (auth.sessionIssue?.type === 'blocked') {
    return (
      <SessionStateScreen
        variant="blocked"
        title={auth.sessionIssue.title}
        message={auth.sessionIssue.message}
        primaryAction={{
          label: 'Atualizar acesso',
          onClick: auth.retrySession,
          icon: <ArrowRightIcon className="h-4 w-4" />,
        }}
        secondaryAction={{
          label: 'Sair',
          onClick: auth.logout,
        }}
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-1">
      {/* Left — Branding */}
      <div className="relative hidden w-0 flex-1 lg:block">
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-gray-900 via-indigo-950 to-gray-900">
          <div className="flex h-full flex-col justify-center px-12 text-white">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm">
                <TvIcon className="h-8 w-8 text-indigo-300" />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight">Painel de Chamadas</h1>
                <p className="text-base text-indigo-300">Orion WaiterCall</p>
              </div>
            </div>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-gray-300">
              Exiba chamadas em tempo real no salão. Ideal para TVs, totens e painéis informativos.
            </p>
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
                  <svg className="h-5 w-5 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-gray-300">Atualização em tempo real via SignalR</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
                  <svg className="h-5 w-5 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 010-7.072m-2.828 9.9a9 9 0 010-12.728"
                    />
                  </svg>
                </div>
                <span className="text-gray-300">Alerta sonoro para novas chamadas</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
                  <svg className="h-5 w-5 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <span className="text-gray-300">Otimizado para TVs e tela cheia</span>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      </div>

      {/* Right — Form Panel */}
      <div className="relative flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24 bg-white">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center text-center">
            <img src={logo} alt="Orion WaiterCall" className="h-auto w-32 sm:w-40 lg:w-48" />
          </div>

          {/* Step indicator */}
          {totalSteps > 1 && step !== STEP_LOGIN && (
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2">
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'h-1.5 rounded-full transition-all duration-300',
                      i + 1 <= currentStepNumber ? 'bg-indigo-600 w-8' : 'bg-gray-200 w-4'
                    )}
                  />
                ))}
              </div>
              <p className="mt-2 text-center text-xs text-gray-400">
                Passo {currentStepNumber} de {totalSteps}
              </p>
            </div>
          )}

          {/* ─── STEP: LOGIN ─── */}
          {step === STEP_LOGIN && (
            <>
              <div className="text-center">
                <h2 className="text-2xl font-bold leading-9 tracking-tight text-gray-900">
                  Painel de Chamadas
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-500">
                  Insira suas credenciais para acessar o painel.
                </p>
              </div>

              <div className="mt-10">
                <form className="space-y-6" onSubmit={handleSubmit(onSubmitLogin)}>
                  {errors.root && (
                    <div className="rounded-md bg-red-50 p-4 border border-red-200">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">{errors.root.message}</h3>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="panel-email" className="block text-sm font-medium leading-6 text-gray-900">
                      Email
                    </label>
                    <div className="relative mt-2 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        id="panel-email"
                        type="email"
                        autoComplete="username"
                        disabled={loginMutation.isPending}
                        className={clsx(
                          'block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all',
                          errors.email ? 'ring-red-400 focus:ring-red-500' : 'ring-gray-300'
                        )}
                        placeholder="Seu email"
                        {...register('email')}
                      />
                    </div>
                    {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="panel-password" className="block text-sm font-medium leading-6 text-gray-900">
                      Senha
                    </label>
                    <div className="relative mt-2 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        id="panel-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        disabled={loginMutation.isPending}
                        className={clsx(
                          'block w-full rounded-md border-0 py-2.5 pl-10 pr-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all',
                          errors.password ? 'ring-red-400 focus:ring-red-500' : 'ring-gray-300'
                        )}
                        placeholder="Sua senha"
                        {...register('password')}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                          ) : (
                            <EyeIcon className="h-5 w-5" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </div>
                    {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>}
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loginMutation.isPending}
                      className="flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors disabled:opacity-70"
                    >
                      {loginMutation.isPending ? (
                        'Entrando...'
                      ) : (
                        <>
                          Entrar
                          <ArrowRightIcon className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}

          {/* ─── STEP: COMPANY SELECTION ─── */}
          {step === STEP_COMPANY && (
            <>
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
                  <BuildingOfficeIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <h2 className="mt-4 text-xl font-bold leading-9 tracking-tight text-gray-900">
                  Selecione a empresa
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-500">
                  Escolha a empresa que será exibida no painel.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                {loadingCompanies ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
                    <p className="mt-3 text-sm text-gray-500">Carregando empresas…</p>
                  </div>
                ) : companies.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">Nenhuma empresa encontrada.</p>
                ) : (
                  companies.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => handleSelectCompany(company)}
                    className="group flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3.5 text-left shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 transition group-hover:bg-indigo-100">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-500 transition group-hover:text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">{company.name}</p>
                    </div>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:text-indigo-500" />
                  </button>
                ))
                )}
              </div>

              <button
                type="button"
                onClick={async () => {
                  cancelAutoResolve();
                  await auth.logout();
                  setCompanies([]);
                  setRestaurants([]);
                  setSelectedCompany(null);
                  setStep(STEP_LOGIN);
                }}
                className="mt-6 flex w-full items-center justify-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Sair e voltar ao login
              </button>
            </>
          )}

          {/* ─── STEP: RESTAURANT SELECTION ─── */}
          {step === STEP_RESTAURANT && (
            <>
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
                  <BuildingStorefrontIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <h2 className="mt-4 text-xl font-bold leading-9 tracking-tight text-gray-900">
                  Selecione o restaurante
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-500">
                  {selectedCompany
                    ? `Restaurantes de ${selectedCompany.name}`
                    : 'Escolha o restaurante para o painel.'}
                </p>
              </div>

              <div className="mt-8 space-y-3">
                {loadingRestaurants ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
                    <p className="mt-3 text-sm text-gray-500">Carregando restaurantes…</p>
                  </div>
                ) : restaurants.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">Nenhum restaurante encontrado.</p>
                ) : (
                  restaurants.map((restaurant) => (
                  <button
                    key={restaurant.id}
                    type="button"
                    onClick={() => handleSelectRestaurant(restaurant)}
                    className="group flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3.5 text-left shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 transition group-hover:bg-indigo-100">
                      <BuildingStorefrontIcon className="h-5 w-5 text-gray-500 transition group-hover:text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">{restaurant.name}</p>
                    </div>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:text-indigo-500" />
                  </button>
                  ))
                )}
              </div>

              {companies.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    cancelAutoResolve();
                    setSelectedCompany(null);
                    setRestaurants([]);
                    setStep(STEP_COMPANY);
                  }}
                  className="mt-6 flex w-full items-center justify-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Voltar para empresas
                </button>
              )}

              {companies.length <= 1 && (
                <button
                  type="button"
                  onClick={async () => {
                    cancelAutoResolve();
                    await auth.logout();
                    setCompanies([]);
                    setRestaurants([]);
                    setSelectedCompany(null);
                    setStep(STEP_LOGIN);
                  }}
                  className="mt-6 flex w-full items-center justify-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Sair e voltar ao login
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <p className="text-xs font-medium text-gray-400">Versão 0.1.0</p>
        </div>
      </div>
    </div>
  );
}
